import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import styles from "./MainLayout.module.css";
import classic from "./MainLayoutClassic.module.css";
import { TopBar } from "@/components/shared/TopBar/TopBar";
import { Header } from "@/components/shared/Header/Header";
import { Sidebar } from "@/components/shared/Sidebar/Sidebar";
import { useBranding } from "@/contexts/shared/BrandingContext";
import { LeaWidget } from "@/components/shared/Lea/LeaWidget";
import { Footer } from "@/components/shared/Footer/Footer";
import { PAGE_BOTTOM_BAR_ID } from "@/components/shared/TableBulkActions/TableBulkActions";
import { useLearningHeartbeat } from "@/hooks/useLearningHeartbeat";
import { useProfileCompletionGate } from "@/hooks/useProfileCompletionGate";
import { useMessagingSocket } from "@/hooks/useMessagingSocket";

export default function MainLayout() {
    const { pathname } = useLocation();
    const scrollRef = useRef<HTMLDivElement>(null);
    const shellRef = useRef<HTMLDivElement>(null);

    // Accumulate active learning time across the authenticated app (drives the
    // learning-time streaks). Cheap Redis-only heartbeat on the server.
    useLearningHeartbeat();

    // Once per session: if the admin marked profile fields as required and the
    // user hasn't filled them, send them to their profile to complete it.
    useProfileCompletionGate();

    // ONE app-wide realtime socket for user-to-user messaging (live messages,
    // read receipts, typing indicators + react-query cache invalidation).
    useMessagingSocket();

    const { uiTheme, uiThemeKnown } = useBranding();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    useEffect(() => {
        scrollRef.current?.scrollTo(0, 0);
        window.scrollTo(0, 0); // phones: the document itself scrolls (see the shell stylesheets)
        setMobileNavOpen(false);
    }, [pathname]);

    // The wheel scrolls the page from anywhere (user: "I should be able to scroll
    // down anywhere and the whole page scrolls"): over the header, the side menu or
    // the footer — the parts outside the page's own scroll area — it moves the page,
    // unless the pointer is over something that can still scroll that way itself
    // (a long side menu, an open list).
    useEffect(() => {
        const root = shellRef.current;
        const page = scrollRef.current;
        if (!root || !page) return;
        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.defaultPrevented) return; // pinch-zoom, or handled already
            const target = e.target instanceof Element ? e.target : null;
            if (!target || page.contains(target)) return; // inside the page: native scrolling
            if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // a sideways gesture
            for (let el: Element | null = target; el && el !== root; el = el.parentElement) {
                if (!/(auto|scroll)/.test(getComputedStyle(el).overflowY) || el.scrollHeight <= el.clientHeight + 1) continue;
                const canScroll = e.deltaY > 0 ? el.scrollTop + el.clientHeight < el.scrollHeight - 1 : el.scrollTop > 0;
                if (canScroll) return;
            }
            const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? page.clientHeight : 1;
            page.scrollBy({ top: e.deltaY * unit });
        };
        root.addEventListener('wheel', onWheel, { passive: true });
        return () => root.removeEventListener('wheel', onWheel);
    }, [uiTheme, uiThemeKnown]);

    // A browser's very first signed-in load: wait for the tenant's theme rather
    // than draw the default shell and rebuild the page in the other one (which
    // reset whatever was open, e.g. the report editor's tab).
    if (!uiThemeKnown) {
        return <div className={styles.shell} ref={shellRef} aria-busy="true" />;
    }

    if (uiTheme === 'classic') {
        return (
            <div className={classic.container} ref={shellRef}>
                <Header onMobileMenuClick={() => setMobileNavOpen((v) => !v)} />
                <div className={`${classic.blob} ${classic.blob1}`} />
                <div className={classic.body}>
                    <div className={`${classic.blob} ${classic.blob2}`} />
                    <div className={`${classic.blob} ${classic.blob3}`} />
                    <div
                        className={`${classic.sidebarWrapper} ${mobileNavOpen ? classic.sidebarWrapperOpen : ''}`}
                        onClick={(e) => { if ((e.target as HTMLElement).closest('a')) setMobileNavOpen(false); }}
                    >
                        <Sidebar />
                    </div>
                    {mobileNavOpen && <div className={classic.navBackdrop} onClick={() => setMobileNavOpen(false)} aria-hidden="true" />}
                    <main className={classic.mainContentArea}>
                        <div className={classic.pageContent} data-page-scroll="true" ref={scrollRef}>
                            <Outlet />
                        </div>
                        {/* Docked bars (TableBulkActions) render here: the scroll area ends above them. */}
                        <div id={PAGE_BOTTOM_BAR_ID} className={classic.pageBottomBar} />
                        <LeaWidget />
                    </main>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className={styles.shell} ref={shellRef}>
            <div className={styles.top}>
                <TopBar />
            </div>

            <main className={styles.main}>
                <div className={styles.pageContent} data-page-scroll="true" ref={scrollRef}>
                    <Outlet />
                </div>
                {/* Docked bars (TableBulkActions) render here: the scroll area ends above them. */}
                <div id={PAGE_BOTTOM_BAR_ID} className={styles.pageBottomBar} />
                <LeaWidget />
            </main>

            <Footer />
        </div>
    );
}
